import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthContext.js'

export interface RoutePresenceUser {
  user_id: string
  display_name: string
  avatar?: string
  editing?: boolean
}

interface PresenceOptions {
  editing?: boolean
}

interface PresenceResponseItem extends RoutePresenceUser {
  joined_at?: number
}

const HEARTBEAT_MS = 800
const RETRY_MAX_MS = 5_000

export function useRoutePresence(
  routeId: string | null | undefined,
  options: PresenceOptions = {},
): RoutePresenceUser[] {
  const auth = useAuth()
  const [users, setUsers] = useState<RoutePresenceUser[]>([])
  const clientId = useMemo(() => createClientId(), [])
  const editingRef = useRef(options.editing === true)

  useEffect(() => {
    editingRef.current = options.editing === true
  }, [options.editing])

  useEffect(() => {
    if (!routeId || auth.status !== 'authed' || !auth.token || !auth.user) {
      setUsers([])
      return
    }

    let closed = false
    let timer: number | null = null
    const controller = new AbortController()
    const endpoint = `/api/presence/${encodeURIComponent(routeId)}`
    const heartbeatEndpoint = `${endpoint}/heartbeat`
    const currentUser = auth.user

    const headers = {
      authorization: `Bearer ${auth.token}`,
      'content-type': 'application/json',
    }

    async function refresh(): Promise<void> {
      await fetch(heartbeatEndpoint, {
        method: 'POST',
        headers,
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          client_id: clientId,
          display_name: currentUser.display_name || currentUser.username,
          editing: editingRef.current,
        }),
      })
      const res = await fetch(endpoint, {
        headers: { authorization: `Bearer ${auth.token}` },
        credentials: 'include',
        signal: controller.signal,
      })
      if (!res.ok) throw new Error(`presence ${res.status}`)
      const data = (await res.json()) as { items?: PresenceResponseItem[] }
      if (!closed) setUsers(mergePresenceUsers(data.items ?? [], currentUser.id))
    }

    function schedule(delay: number): void {
      if (closed) return
      timer = window.setTimeout(() => {
        void refresh()
          .then(() => schedule(HEARTBEAT_MS))
          .catch(() => schedule(Math.min(delay * 2, RETRY_MAX_MS)))
      }, delay)
    }

    function leave(): void {
      const url = `${heartbeatEndpoint}?client_id=${encodeURIComponent(clientId)}`
      void fetch(url, {
        method: 'DELETE',
        headers: { authorization: `Bearer ${auth.token}` },
        credentials: 'include',
        keepalive: true,
      }).catch(() => {})
    }

    void refresh()
      .then(() => schedule(HEARTBEAT_MS))
      .catch(() => schedule(HEARTBEAT_MS))
    window.addEventListener('pagehide', leave)

    return () => {
      closed = true
      if (timer) window.clearTimeout(timer)
      controller.abort()
      window.removeEventListener('pagehide', leave)
      leave()
    }
  }, [auth.status, auth.token, auth.user, clientId, routeId])

  return users
}

function mergePresenceUsers(
  items: PresenceResponseItem[],
  currentUserId: string,
): RoutePresenceUser[] {
  const byUser = new Map<string, RoutePresenceUser & { joined_at?: number }>()
  for (const item of items) {
    const existing = byUser.get(item.user_id)
    const next = {
      user_id: item.user_id,
      display_name: item.display_name || shortUserId(item.user_id),
      avatar: item.avatar,
      editing: item.editing === true,
      joined_at: item.joined_at,
    }
    if (!existing) {
      byUser.set(item.user_id, next)
      continue
    }
    byUser.set(item.user_id, {
      ...existing,
      display_name: existing.display_name || next.display_name,
      avatar: existing.avatar ?? next.avatar,
      editing: existing.editing === true || next.editing === true,
      joined_at: Math.min(existing.joined_at ?? Number.MAX_SAFE_INTEGER, next.joined_at ?? 0),
    })
  }

  return [...byUser.values()]
    .sort((a, b) => {
      if (a.user_id === currentUserId) return -1
      if (b.user_id === currentUserId) return 1
      return (a.joined_at ?? 0) - (b.joined_at ?? 0)
    })
    .map(({ joined_at: _joinedAt, ...user }) => user)
}

function createClientId(): string {
  const randomUuid = globalThis.crypto?.randomUUID?.()
  if (randomUuid) return randomUuid
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function shortUserId(userId: string): string {
  return userId.length <= 8 ? userId : userId.slice(0, 8)
}
